import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WorkflowRunStatusEvent, WorkflowSimpleV1Read } from '@ca/core/models';
import { CacheBucket, HttpCacheManager, withCache } from '@ngneat/cashew';
import { TranslateService } from '@ngx-translate/core';
import { Observable, OperatorFunction, forkJoin, iif, of, throwError, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { retry } from '../decorators/retry';
import { WorkflowType } from '../enums';
import { ArchivedWorkflowRun, WorkflowSimple, WorkflowSubmissionData } from '../models';
import { ListResponse } from '../models/list-response.model';
import { ModuleV1Read, VersionReadModel } from '../models/module-v1-read.model';
import { SingleResponse } from '../models/single-response.model';
import { UserInfo } from '../models/user-info.model';
import { WorkflowCreate } from '../models/workflow-create.model';
import { WorkflowMl } from '../models/workflow-ml';
import { WorkflowUpdate } from '../models/workflow-update.model';
import { WorkflowSubmissionProducerRead, WorkflowV1Read, WorkflowV1Run } from '../models/workflow-v1-read.model';
import { ModuleVersionService } from './module-version.service';
import { UserService } from './user.service';
import { WorkflowRunQueueService } from './workflow-run-queue.service';

@Injectable()
export class WorkflowV1Service {
    private readonly baseUrl: string = '/api/module_manager/v1/workspace/workflows';
    private readonly baseUrlPublic: string = '/api/module_manager/v1/public/workflows';
    private readonly baseUrlAdmin: string = '/api/module_manager/v1/admin/workflows';
    private readonly baseUrlSubmissionsAdmin: string = '/api/module_manager/v1/admin/workflowsubmissions';
    private readonly headers = { 'Content-Type': 'application/query.api+json' };

    private buckets: Map<string, CacheBucket> = new Map<string, CacheBucket>();

    constructor(
        private httpClient: HttpClient,
        private userService: UserService,
        private moduleVersionService: ModuleVersionService,
        private workflowRunQueueService: WorkflowRunQueueService,
        private translateService: TranslateService,
        private httpCacheManager: HttpCacheManager,
    ) {
        this.buckets.set(WorkflowType.SEGMENTATION, new CacheBucket());
        this.buckets.set(WorkflowType.SEGMENTATION_TRAINING, new CacheBucket());
        this.workflowRunQueueService.workflowCompleted$.subscribe((event: WorkflowRunStatusEvent) => this.handleWorkflowFinishedEvent(event));
    }

    create(data: WorkflowCreate): Observable<SingleResponse<WorkflowV1Read>> {
        return this.httpClient.post<SingleResponse<WorkflowV1Read>>(this.baseUrl, data, {
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        });
    }

    @retry()
    update(id: string, data: WorkflowUpdate, isAdmin = false): Observable<SingleResponse<WorkflowV1Read>> {
        const baseUrl = isAdmin ? this.baseUrlAdmin : this.baseUrl;
        return this.httpClient.patch<SingleResponse<WorkflowV1Read>>(`${baseUrl}/${id}`, data, {
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        });
    }

    delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
    }

    getPublicList(cacheType?: WorkflowType, params?: HttpParams, appendUserInfo = true): Observable<ListResponse<WorkflowV1Run>> {
        return this.getList(cacheType, params, false, appendUserInfo);
    }

    getMlAdminConfigList(): Observable<WorkflowMl[]> {
        return this.getMlAdminList();
    }

    getPublicOverviewList(params?: HttpParams, appendUserInfo = true): Observable<ListResponse<WorkflowSimple>> {
        return this.getPublicOverview(params, appendUserInfo);
    }

    getPrivateOverviewList(params?: HttpParams): Observable<ListResponse<WorkflowSimple>> {
        return this.getPrivateOverview(params);
    }

    getById(id: string): Observable<WorkflowV1Read> {
        return this.httpClient.get(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
            map((res: { data: WorkflowV1Read }) => res.data),
            tap((workflow: WorkflowV1Read) => {
                // instantiate modules (with their selectedModuleVersion), so that we have access to its methods:
                if (workflow.usedModules != null) {
                    workflow.usedModules = workflow.usedModules.map((m: ModuleV1Read) => {
                        const version: VersionReadModel | undefined = workflow.usedModuleVersions.find((mv: VersionReadModel) => mv.moduleId === m.id);
                        return new ModuleV1Read(m, version);
                    });
                }

                return workflow;
            }),
        );
    }

    getSubmissions(id: string): Observable<WorkflowSubmissionProducerRead[]> {
        return this.httpClient
            .get(`${this.baseUrl}/submissions/${id}`, { headers: this.headers })
            .pipe(map((res: { data: WorkflowSubmissionProducerRead[] }) => res.data));
    }

    getSuccessorsWorkflowById(id: string, type: WorkflowType): Observable<WorkflowV1Run> {
        return this.httpClient.get(`${this.baseUrlPublic}/successorWorkflow/${id}/${type}`, { headers: this.headers }).pipe(
            map((res: { data: WorkflowV1Run }) => {
                return res.data;
            }),
            tap((workflow: WorkflowV1Run) => {
                // instantiate modules (with their selectedModuleVersion), so that we have access to its methods:
                if (workflow.usedModules != null) {
                    workflow.usedModules = workflow.usedModules.map((m: ModuleV1Read) => {
                        const version: VersionReadModel | undefined = workflow.usedModuleVersions.find((mv: VersionReadModel) => mv.moduleId === m.id);
                        return new ModuleV1Read(m, version);
                    });
                }

                return workflow;
            }),
        );
    }

    getPublicById(id: string): Observable<WorkflowV1Run> {
        return <Observable<WorkflowV1Run>>(
            this.httpClient.get(`${this.baseUrlPublic}/${id}`, { headers: this.headers }).pipe(map((res: { data: WorkflowV1Run }) => res.data))
        );
    }

    getByIdAsAdmin(id: string): Observable<WorkflowV1Read> {
        return <Observable<WorkflowV1Read>>(
            this.httpClient
                .get(`${this.baseUrlSubmissionsAdmin}/${id}/workflow`, { headers: this.headers })
                .pipe(map((res: { data: WorkflowV1Read }) => res.data))
        );
    }

    publishWorkflow(id: string, payload: WorkflowSubmissionData): Observable<Object> {
        return this.httpClient.post(`/api/module_manager/v1/workspace/workflows/${id}/submissions`, payload);
    }

    createWorkflowFromArchivedWorkflowRun(archivedWorkflowRun: ArchivedWorkflowRun, workflowTitle?: string): Observable<WorkflowV1Read> {
        if (!archivedWorkflowRun.workflowSpecification.workflow) {
            return throwError(this.translateService.instant('WORKFLOWS.MESSAGES.SPECIFICATION_MISSING'));
        }

        return this.prepareWorkflowFromArchivedWorkflowRun(archivedWorkflowRun, workflowTitle);
    }

    invalidateCache(workflowType: WorkflowType): void {
        const bucket = this.buckets.get(workflowType);
        if (bucket) {
            this.httpCacheManager.delete(bucket);
        }
    }

    private getList(cacheType?: WorkflowType, params?: HttpParams, isPrivate = false, appendUserInfo: boolean = true): Observable<ListResponse<WorkflowV1Run>> {
        const baseUrl = isPrivate ? this.baseUrl : this.baseUrlPublic;

        const options: {
            headers?: {
                [header: string]: string | string[];
            };
            params?: HttpParams;
            context?: HttpContext;
        } = {};
        options.headers = this.headers;
        if (params) {
            options.params = params;
        }

        if (cacheType) {
            options.context = withCache({ cache: true, bucket: this.buckets.get(cacheType) });
        }

        return this.httpClient.get<ListResponse<WorkflowV1Run>>(baseUrl, options).pipe(
            appendUserInfo ? this.appendUserInfo() : map(res => res),
            map((res: ListResponse<WorkflowV1Run>) => this.prepareWorkflowsUsedModules(res)),
        );
    }

    private getMlAdminList(): Observable<WorkflowMl[]> {
        return this.httpClient.get<WorkflowMl[]>(`${this.baseUrlPublic}/overview/ml`);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getPublicOverview(params?: any, appendUserInfo: boolean = true): Observable<ListResponse<WorkflowSimple>> {
        if (params?.cache$ && params?.type && this.buckets.has(params.type)) {
            params.bucket$ = this.buckets.get(params?.type);
        }

        return this.httpClient
            .get<ListResponse<WorkflowSimpleV1Read>>(`${this.baseUrlPublic}/overview`, {
                params,
                headers: this.headers,
            })
            .pipe(appendUserInfo ? this.appendUserInfo() : map(res => res));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getPrivateOverview(params?: any, appendUserInfo: boolean = true): Observable<ListResponse<WorkflowSimple>> {
        if (params?.cache$ && params?.type && this.buckets.has(params.type)) {
            params.bucket$ = this.buckets.get(params?.type);
        }

        return this.httpClient
            .get<ListResponse<WorkflowSimpleV1Read>>(`${this.baseUrl}/overview`, {
                params,
                headers: this.headers,
            })
            .pipe(appendUserInfo ? this.appendUserInfo() : map(res => res));
    }

    private prepareWorkflowFromArchivedWorkflowRun(archivedWorkflowRun: ArchivedWorkflowRun, workflowTitle?: string): Observable<WorkflowV1Read> {
        const flow = archivedWorkflowRun.workflowSpecification.workflow.flow;
        flow.forEach(flowUnit => {
            if (flowUnit.run.input) {
                Object.assign(flowUnit.run.input, archivedWorkflowRun.inputs[flowUnit.id]);
            }
        });

        const moduleVersionIds: string[] = flow.map(fu => fu.run.module);

        return iif(
            () => moduleVersionIds.length > 0,
            forkJoin(moduleVersionIds.map((mvId: string) => this.moduleVersionService.getModuleByVersionId(mvId))),
            of([]),
        ).pipe(
            map((models: ModuleV1Read[]) => {
                const moduleVersions: VersionReadModel[] = [];
                for (const model of models) {
                    if (model.latestPublicVersion != null) {
                        moduleVersions.push(model.latestPublicVersion);
                    }
                    if (model.draftVersion != null) {
                        moduleVersions.push(model.draftVersion);
                    }
                }

                return {
                    id: archivedWorkflowRun.workflowId,
                    title: workflowTitle || archivedWorkflowRun.workflowTitle,
                    specification: archivedWorkflowRun.workflowSpecification,
                    usedModules: models,
                    usedModuleVersions: moduleVersions,
                } as WorkflowV1Read;
            }),
        );
    }

    private appendUserInfo(): OperatorFunction<ListResponse<WorkflowV1Read>, ListResponse<WorkflowV1Read>> {
        return input$ =>
            input$.pipe(
                switchMap((res: ListResponse<WorkflowV1Read>) => {
                    const ids = res.data.map(workflow => workflow.producerId);
                    return zip(this.userService.getUserInfos$(ids), of(res));
                }),
                map((zipped: [UserInfo[], ListResponse<WorkflowV1Read>]) => {
                    const [userInfos, workflows] = zipped;
                    workflows.data.forEach(workflow => (workflow.author = userInfos.find(user => user.id === workflow.producerId)));
                    return workflows;
                }),
            );
    }

    private prepareWorkflowsUsedModules(listResponse: ListResponse<WorkflowV1Run>): ListResponse<WorkflowV1Run> {
        const workflows = listResponse.data.map((workflow: WorkflowV1Run) => {
            // instantiate modules (with their selectedModuleVersion), so that we have access to its methods:
            if (workflow.usedModules != null) {
                const usedModules = workflow.usedModules.map((m: ModuleV1Read) => {
                    const version: VersionReadModel | undefined = workflow.usedModuleVersions.find((mv: VersionReadModel) => mv.moduleId === m.id);
                    return new ModuleV1Read(m, version);
                });
                return { ...workflow, ...{ usedModules } };
            }
            return workflow;
        });
        return { ...listResponse, ...{ data: workflows } };
    }

    // delete cache bucket on workflow finish event to get fresh data on next request
    private handleWorkflowFinishedEvent(event: WorkflowRunStatusEvent): void {
        const bucket = this.buckets.get(event.workflowType);
        if (bucket) {
            this.httpCacheManager.delete(bucket);
        }
    }
}
